import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { CATERING, BRAZE_COOKIES } from 'common/constants/SharedConstants';
import {
  updateGustUserContactDetails,
  updateCateringSomeOneElseContactDetails,
  validateCheckoutDetails,
  setMarketingOptDetails,
} from 'common/utilities/CheckoutUtils';
import { getGuestContent } from 'common/utilities/content';
import { login, createAccount } from 'common/utilities/LoginUtils';
import { embeddedErrorMessageAnalyticFn, isSupportedTenantCode } from 'common/utilities/utils';

import CheckoutContactInfoView from 'organisms/CheckoutContactInfo/presentation/AU/CheckoutContactInfoView';
import {
  onFormElementChangeHandler,
  prepareFormElmentConfig,
  resetGuestFormValue,
} from 'organisms/CheckoutContactInfo/Utils/CheckoutContactInfoUtils';

const signInBtnClickHandler = () => {
  login();
};

const signUpBtnClickHandler = () => {
  createAccount();
};

const continueAsaGustBtnClickHandler = (checkoutDetails, updateCheckoutDetails) => {
  if (!checkoutDetails?.isEnableGustUser) {
    updateCheckoutDetails({ isEnableGustUser: true });
  }
};

const CheckoutContactInfo = props => {
  const { checkoutDetails, updateCheckoutDetails, displayFor = null } = props;
  const {
    dispatch,
    userFullName,
    userFirstName,
    userLastName,
    userPhone,
    userEmail,
    termsPolicyCheckbox,
    guestCheckOutFieldsValidated,
    cateringSomeOneElseContactDetails,
  } = checkoutDetails;
  const { contentReducer } = useSelector(data => data);

  useEffect(() => {
    dispatch(getGuestContent());
    updateCheckoutDetails({ isEnableFullName: !isSupportedTenantCode() });
    resetGuestFormValue(updateCheckoutDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (contentReducer?.guestContentData?.items?.[0]?.fields?.formField.length > 0) {
      const cookiesSelected = window?.OnetrustActiveGroups ? JSON.stringify(window?.OnetrustActiveGroups)?.includes(BRAZE_COOKIES) : false;
      updateCheckoutDetails({
        guestCheckoutFormConfig: {
          fieldsConfig: prepareFormElmentConfig(contentReducer,cookiesSelected),
          isFormValid: false,
          isFormReadOnly: checkoutDetails?.isClickAddPayment,
          onFormChangeHandler: onFormElementChangeHandler,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentReducer?.guestContentData, window?.OnetrustActiveGroups]);

  useEffect(() => {
    if (checkoutDetails?.guestCheckoutFormConfig?.fieldsConfig?.length) {
      updateCheckoutDetails({
        guestCheckoutFormConfig: {
          ...checkoutDetails?.guestCheckoutFormConfig,
          isFormReadOnly: checkoutDetails?.isClickAddPayment,
        },
      });
      setMarketingOptDetails(checkoutDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutDetails?.isClickAddPayment]);

  useEffect(() => {
    embeddedErrorMessageAnalyticFn(document.title, userFullName?.error, userFullName?.errorMessage);
  }, [userFullName?.errorMessage, userFullName?.error]);

  useEffect(() => {
    embeddedErrorMessageAnalyticFn(document.title, userFirstName?.error, userFirstName?.errorMessage);
  }, [userFirstName?.errorMessage, userFirstName?.error]);

  useEffect(() => {
    embeddedErrorMessageAnalyticFn(document.title, userLastName?.error, userLastName?.errorMessage);
  }, [userLastName?.errorMessage, userLastName?.error]);

  useEffect(() => {
    embeddedErrorMessageAnalyticFn(document.title, userPhone?.error, userPhone?.errorMessage);
  }, [userPhone?.errorMessage, userPhone?.error]);

  useEffect(() => {
    embeddedErrorMessageAnalyticFn(document.title, userEmail?.error, userEmail?.errorMessage);
  }, [userEmail?.errorMessage, userEmail?.error]);

  // here we will validate the data and enable payment button based on required data
  useEffect(() => {
    validateCheckoutDetails(checkoutDetails, updateCheckoutDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userFullName,
    userFirstName,
    userLastName,
    userEmail,
    userPhone,
    termsPolicyCheckbox,
    guestCheckOutFieldsValidated,
    cateringSomeOneElseContactDetails?.userFirstName,
    cateringSomeOneElseContactDetails?.userLastName,
    cateringSomeOneElseContactDetails?.userEmail,
    cateringSomeOneElseContactDetails?.userPhone,
  ]);

  useEffect(() => {
    displayFor === CATERING &&
      embeddedErrorMessageAnalyticFn(
        document.title,
        cateringSomeOneElseContactDetails?.userFirstName?.error,
        cateringSomeOneElseContactDetails?.userFirstName?.errorMessage,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cateringSomeOneElseContactDetails?.userFirstName?.errorMessage,
    cateringSomeOneElseContactDetails?.userFirstName?.error,
  ]);

  useEffect(() => {
    displayFor === CATERING &&
      embeddedErrorMessageAnalyticFn(
        document.title,
        cateringSomeOneElseContactDetails?.userLastName?.error,
        cateringSomeOneElseContactDetails?.userLastName?.errorMessage,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cateringSomeOneElseContactDetails?.userLastName?.errorMessage,
    cateringSomeOneElseContactDetails?.userLastName?.error,
  ]);

  useEffect(() => {
    displayFor === CATERING &&
      embeddedErrorMessageAnalyticFn(
        document.title,
        cateringSomeOneElseContactDetails?.userPhone?.error,
        cateringSomeOneElseContactDetails?.userPhone?.errorMessage,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cateringSomeOneElseContactDetails?.userPhone?.errorMessage, cateringSomeOneElseContactDetails?.userPhone?.error]);

  useEffect(() => {
    displayFor === CATERING &&
      embeddedErrorMessageAnalyticFn(
        document.title,
        cateringSomeOneElseContactDetails?.userEmail?.error,
        cateringSomeOneElseContactDetails?.userEmail?.errorMessage,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cateringSomeOneElseContactDetails?.userEmail?.errorMessage, cateringSomeOneElseContactDetails?.userEmail?.error]);

  return (
    <CheckoutContactInfoView
      {...props}
      updateGustUserContactDetails={event =>
        updateGustUserContactDetails(event, props?.checkoutDetails, props?.updateCheckoutDetails)
      }
      updateCateringSomeOneElseContactDetails={event =>
        updateCateringSomeOneElseContactDetails(event, props?.checkoutDetails, props?.updateCheckoutDetails)
      }
      signInBtnClickHandler={signInBtnClickHandler}
      signUpBtnClickHandler={signUpBtnClickHandler}
      continueAsaGustBtnClickHandler={() =>
        continueAsaGustBtnClickHandler(props?.checkoutDetails, props?.updateCheckoutDetails)
      }
    />
  );
};

export default CheckoutContactInfo;
